/**
 * Config module types and Zod schemas.
 *
 * Wire format is snake_case JSON (RemoteConfigurationSchema).
 * Internal representation is camelCase TypeScript (ClientConfigurationSchema).
 */

import { z } from 'zod';

// ─── Remote schema (snake_case JSON from API) ─────────────────────────────────

export const RemoteConfigurationSchema = z.object({
  service: z.object({
    version: z.string().optional(),
    api_url: z.string(),
    origins_web: z.string(),
    stage: z.string(),
  }),
  auth: z.object({
    publishable_key: z.string().nullable(),
  }),
  sentry: z
    .object({
      dsn: z.string().nullable(),
      traces_sample_rate: z.number(),
      environment: z.string(),
    })
    .optional(),
  features: z.object({
    presigned_uploads: z.boolean(),
    telegram_bot_name: z.string(),
    waitlist_url: z.string().nullable(),
  }),
});

export type RemoteConfiguration = z.infer<typeof RemoteConfigurationSchema>;

// ─── Client schema (camelCase TypeScript) ─────────────────────────────────────

export const ClientConfigurationSchema = z.object({
  service: z.object({
    version: z.string().optional(),
    apiUrl: z.string(),
    originsWeb: z.string(),
    stage: z.string(),
  }),
  auth: z.object({
    publishableKey: z.string(),
  }),
  sentry: z.object({
    dsn: z.string().nullable(),
    tracesSampleRate: z.number(),
    environment: z.string(),
  }),
  features: z.object({
    presignedUploads: z.boolean(),
    telegramBotName: z.string(),
    waitlistUrl: z.string().nullable(),
  }),
});

export type ClientConfiguration = z.infer<typeof ClientConfigurationSchema>;

// ─── Supporting types ─────────────────────────────────────────────────────────

export interface ResolveConfigOptions {
  overrides?: {
    apiUrl?: string;
    externalApiUrl?: string;
    wsUrl?: string;
    baseHostname?: string;
    clerkPublishableKey?: string;
    telegramBotName?: string;
    waitlistUrl?: string;
  };
}

export interface ConventionDefaults {
  apiUrl: string;
}

// ─── Custom error ─────────────────────────────────────────────────────────────

export class ConfigurationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfigurationError';
  }
}
